import { getProxyAuthHeaders } from '@/auth/imsTokenStorage';
import { config } from '@/config/env';
import { logger } from '@/logger';

// OAuth Scopes for Square
const SQUARE_OAUTH_SCOPES = [
	'CUSTOMERS_READ',
	'ITEMS_READ',
	'PAYMENTS_READ',
	'MERCHANT_PROFILE_READ',
	'ORDERS_READ',
].join(' ');

// Browser storage keys for OAuth flow state and local environment preference.
const SQUARE_STATE_SESSION_KEY = 'squareOAuthState';
const SQUARE_SANDBOX_MODE_KEY = 'squareSandboxMode';

export interface SquareTokenData {
	access_token: string;
	token_type: string;
	expires_at: string; // ISO date string
	merchant_id: string;
	refresh_token: string;
	short_lived: boolean;
	refresh_token_expires_at?: string;
}

interface SquareConnectionExpiry {
	square_token_expires_at: string;
}

export const hasUnexpiredSquareConnection = (
	connection: SquareConnectionExpiry | null | undefined,
): boolean => {
	if (!connection) return false;
	const expiresAt = new Date(connection.square_token_expires_at);
	return !isNaN(expiresAt.getTime()) && expiresAt > new Date();
};

/**
 * Get the redirect URI for Square OAuth callback
 */
export const getSquareAuthRedirectUri = (): string => {
	return `${window.location.origin}/auth/square`;
};

/**
 * Generate a random state parameter for CSRF protection
 */
const generateState = (): string => {
	const array = new Uint8Array(32);
	crypto.getRandomValues(array);
	return Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join(
		'',
	);
};

/**
 * Check if Square sandbox mode is enabled
 */
export const isSquareSandboxMode = (): boolean => {
	return localStorage.getItem(SQUARE_SANDBOX_MODE_KEY) === 'true';
};

/**
 * Enable or disable Square sandbox mode.
 */
export const setSquareSandboxMode = (enabled: boolean): void => {
	localStorage.setItem(SQUARE_SANDBOX_MODE_KEY, String(enabled));
	logger.info(`Square sandbox mode ${enabled ? 'enabled' : 'disabled'}`);
};

/**
 * Get the Square OAuth config based on current sandbox mode
 */
const getSquareOAuthConfig = (): {
	applicationId: string;
	oauthBaseUrl: string;
} => {
	if (isSquareSandboxMode()) {
		return {
			applicationId: config.SQUARE_SANDBOX_APPLICATION_ID,
			oauthBaseUrl: config.SQUARE_SANDBOX_OAUTH_BASE_URL,
		};
	}
	return {
		applicationId: config.SQUARE_APPLICATION_ID,
		oauthBaseUrl: config.SQUARE_OAUTH_BASE_URL,
	};
};

/**
 * Redirect to Square OAuth authorization page
 */
export const redirectToSquareOAuth = (): void => {
	const { applicationId, oauthBaseUrl } = getSquareOAuthConfig();
	const oauthEndpoint = `${oauthBaseUrl}/oauth2/authorize`;
	const redirectUri = getSquareAuthRedirectUri();

	// Generate and store state for CSRF protection
	const state = generateState();
	sessionStorage.setItem(SQUARE_STATE_SESSION_KEY, state);

	const oauthParams = new URLSearchParams({
		client_id: applicationId,
		response_type: 'code',
		redirect_uri: redirectUri,
		scope: SQUARE_OAUTH_SCOPES,
		state,
	});

	const oauthUrl = `${oauthEndpoint}?${oauthParams.toString()}`;
	logger.info(`Redirecting to Square OAuth: ${oauthUrl}`);

	window.location.href = oauthUrl;
};

/**
 * Extract code and state from URL query params
 */
export const extractCodeFromUrl = (): {
	code: string | null;
	state: string | null;
	error: string | null;
	errorDescription: string | null;
} => {
	const params = new URLSearchParams(window.location.search);

	const error = params.get('error');
	const errorDescription = params.get('error_description');

	if (error) {
		return { code: null, state: null, error, errorDescription };
	}

	const code = params.get('code');
	const state = params.get('state');

	return { code, state, error: null, errorDescription: null };
};

/**
 * Validate the state parameter against the stored value (CSRF protection)
 */
export const validateState = (state: string | null): boolean => {
	if (!state) return false;
	const storedState = sessionStorage.getItem(SQUARE_STATE_SESSION_KEY);
	return state === storedState;
};

/**
 * Clear the stored state parameter
 */
export const clearStoredState = (): void => {
	sessionStorage.removeItem(SQUARE_STATE_SESSION_KEY);
};

/**
 * Clear URL query parameters
 */
export const clearUrlParams = (): void => {
	window.history.replaceState({}, '', window.location.pathname);
};

/**
 * Exchange the OAuth code for a token.
 *
 * ui-proxy stores the resulting connection itself, against the account named by the IMS
 * token these headers carry. It used to be this window's job: the popup posted the merchant
 * id to its opener, which saved it. When the opener was unreachable the merchant id was lost
 * even though Square had already begun delivering that merchant's sales — so the write now
 * happens where the exchange does.
 *
 * `persisted` says whether that write succeeded. The exchange still succeeds when it did
 * not, because the merchant has authorized us in Square by then either way.
 */
export const exchangeCodeForToken = async (
	code: string,
): Promise<
	SquareTokenData & { persisted: boolean; persist_reason?: string }
> => {
	const redirectUri = getSquareAuthRedirectUri();
	const sandbox = isSquareSandboxMode();

	const response = await fetch(`${config.UI_PROXY_URL}/square-proxy/token`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			...getProxyAuthHeaders(),
		},
		body: JSON.stringify({
			code,
			redirectUri,
			sandbox,
		}),
	});

	if (!response.ok) {
		const errorData = await response.json();
		logger.error('Failed to exchange Square code for token:', errorData);
		throw new Error(errorData?.error || 'Failed to exchange code for token');
	}

	return response.json();
};
